import { logger } from '../../utils/logger.js'
import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { SettingsStorage } from '../../storage/settings-storage.js'
import { userStorage } from '../../storage/user-storage.js'
import { authService } from '../../services/auth/auth-service.js'
import type { AuthState } from '../../services/auth/auth-context.js'
import {
  listCredentials,
  deletePasskey,
  registerPasskey,
  type PasskeyInfo,
  type LinkedProvider,
} from '../../services/auth/credential-service.js'
import { themeManager } from '../../services/theme-manager.js'
import {
  permissionManager,
  type PermissionStatus,
} from '../../services/permissions/permission-manager.js'
import { router } from '../../utils/router.js'
import { blockStore } from '../../services/moderation/block-store.js'
import type { BlockedUser } from '../../services/moderation/moderation-api.js'
import type { UserSettings } from '../../database/schema'

const log = logger.create('profile')

// Material Web Components
import '@material/web/switch/switch.js'
import '@material/web/radio/radio.js'
import '@material/web/select/outlined-select.js'
import '@material/web/iconbutton/icon-button.js'
import '@material/web/select/select-option.js'
import '@material/web/button/outlined-button.js'
import '@material/web/button/text-button.js'
import './cairn-profile.js'
import './cairn-storage-settings.js'
import './cairn-subscription.js'

interface SettingsFormData {
  unitSystem: 'metric' | 'imperial'
  theme: 'light' | 'dark' | 'auto'
  coordinateFormat: 'DD' | 'DMS' | 'DDM' | 'MGRS' | 'UTM'
  coordinatePrecision: {
    ddDecimals: number
    dmsSecondDecimals: number
    directionFormat: 'signed' | 'cardinal'
    mgrsPrecision: 1 | 2 | 3 | 4 | 5
    utmShowZone: boolean
  }
  contactSharing?: {
    shareEmail: 'always' | 'never' | 'ask'
    sharePhone: 'always' | 'never' | 'ask'
    shareEmergency: 'always' | 'never' | 'ask'
  }
}

type SettingsPage =
  | 'root'
  | 'profile'
  | 'preferences'
  | 'units'
  | 'theme'
  | 'tracking'
  | 'permissions'
  | 'privacy'
  | 'security'
  | 'storage'
  | 'subscription'

@customElement('cairn-settings')
export class CairnSettings extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      background: var(--cairn-background-color, #f8fafc);
      overflow: hidden;
    }

    .settings-header {
      background: var(--cairn-surface-color, #ffffff);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      padding: 12px 16px;
      display: none;
      align-items: center;
      gap: 12px;
      flex: 0 0 auto;
    }

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
    }

    .back-button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .back-button .material-icons {
      font-size: 24px;
      font-family: 'Material Symbols Outlined';
    }

    .settings-title {
      font-size: 20px;
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    /* ---- Layout shell ---- */
    .settings-layout {
      display: flex;
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }

    /* ---- Sidebar (desktop) / List (mobile) ---- */
    .settings-nav {
      display: flex;
      flex-direction: column;
      background: var(--cairn-surface-color, #ffffff);
      border-right: 1px solid var(--cairn-border-color, #e2e8f0);
      overflow-y: auto;
    }

    .nav-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 20px;
      border: none;
      background: none;
      cursor: pointer;
      text-align: left;
      color: var(--cairn-text-color, #1e293b);
      transition: background 0.15s ease;
      border-left: 3px solid transparent;
    }

    .nav-item:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .nav-item.active {
      background: var(--cairn-surface-hover-color, #f1f5f9);
      border-left-color: var(--cairn-primary-color, #3b82f6);
    }

    .nav-item .material-icons {
      font-size: 22px;
      font-family: 'Material Symbols Outlined';
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .nav-item.active .material-icons {
      color: var(--cairn-primary-color, #3b82f6);
    }

    .nav-item-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .nav-item-label {
      font-size: 15px;
      font-weight: 500;
    }

    .nav-item-description {
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .nav-item-chevron {
      margin-left: auto;
      font-size: 20px !important;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .nav-sub-items {
      overflow: hidden;
      max-height: 0;
      transition: max-height 0.25s ease-out;
    }

    .nav-sub-items.expanded {
      max-height: 200px;
      transition: max-height 0.3s ease-in;
    }

    .nav-sub-item {
      padding-left: 44px;
    }

    .nav-sub-item .material-icons {
      font-size: 18px;
    }

    .nav-item-chevron {
      transition: transform 0.25s ease;
    }

    .nav-item-chevron.rotated {
      transform: rotate(90deg);
    }

    /* ---- Content pane ---- */
    .settings-content {
      flex: 1;
      overflow-y: auto;
      padding: 24px;
      min-height: 0;
    }

    .content-inner {
      max-width: 600px;
    }

    .section-title {
      font-size: 18px;
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 24px 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .section-title .material-icons {
      font-size: 22px;
      font-family: 'Material Symbols Outlined';
      color: var(--cairn-primary-color, #3b82f6);
    }

    .setting-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 0;
    }

    .setting-item + .setting-item {
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .setting-info {
      flex: 1;
      margin-right: 16px;
    }

    .setting-label {
      font-size: 15px;
      font-weight: 500;
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 2px 0;
    }

    .setting-description {
      font-size: 13px;
      color: var(--cairn-text-secondary-color, #64748b);
      margin: 0;
    }

    .setting-control {
      flex-shrink: 0;
    }

    .radio-group {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .radio-option {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 0;
    }

    .radio-option label {
      color: var(--cairn-text-color, #1e293b);
      font-size: 14px;
      cursor: pointer;
    }

    md-outlined-select {
      width: 100%;
      --md-outlined-select-text-field-container-height: 48px;
      --md-outlined-select-text-field-container-shape: 8px;
    }

    md-switch {
      --md-switch-track-width: 48px;
      --md-switch-track-height: 24px;
      --md-switch-touch-target-size: 20px;
      --md-switch-selected-handle-height: 20px;
      --md-switch-selected-handle-width: 20px;
      --md-switch-icon-size: 16px;
    }

    md-radio {
      --md-radio-icon-size: 16px;
    }

    .perm-icon-btn {
      --md-icon-button-icon-size: 24px;
      --md-icon-button-state-layer-height: 40px;
      --md-icon-button-state-layer-width: 40px;
    }

    .perm-icon-btn .material-icons {
      font-family: 'Material Symbols Outlined';
    }

    .perm-icon-btn.granted .material-icons {
      color: #16a34a;
    }

    .perm-icon-btn.denied .material-icons,
    .perm-icon-btn.prompt .material-icons {
      color: #dc2626;
    }

    .perm-icon-btn.unavailable {
      opacity: 0.5;
      cursor: default;
    }

    .perm-icon-btn.unavailable .material-icons {
      color: var(--cairn-text-secondary-color, #94a3b8);
    }

    /* ---- Security section ---- */
    .credential-row {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 0;
    }

    .credential-row + .credential-row {
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .credential-icon {
      width: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .credential-icon svg {
      width: 22px;
      height: 22px;
      fill: currentColor;
    }

    .credential-icon .material-icons {
      font-size: 22px;
      font-family: 'Material Symbols Outlined';
    }

    .credential-info {
      flex: 1;
      min-width: 0;
    }

    .credential-name {
      font-size: 14px;
      font-weight: 500;
      color: var(--cairn-text-color, #1e293b);
    }

    .credential-detail {
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .credential-status {
      flex-shrink: 0;
    }

    .credential-status .material-icons {
      font-size: 20px;
      font-family: 'Material Symbols Outlined';
      color: var(--cairn-success-color, #22c55e);
    }

    .section-divider {
      border: none;
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
      margin: 24px 0;
    }

    .platform-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 500;
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-text-secondary-color, #64748b);
      margin-bottom: 16px;
    }

    .platform-badge .material-icons {
      font-size: 16px !important;
      font-family: 'Material Symbols Outlined';
    }

    .section-note {
      font-size: 13px;
      color: var(--cairn-text-secondary-color, #64748b);
      margin-top: 16px;
      padding: 12px;
      background: var(--cairn-surface-hover-color, #f1f5f9);
      border-radius: 8px;
    }

    .perm-warning {
      display: flex;
      align-items: flex-start;
      gap: 6px;
      font-size: 12px;
      color: #b45309;
      padding: 6px 10px;
      margin: -4px 0 8px 30px;
      background: #fef3c7;
      border-radius: 6px;
    }

    .perm-warning .material-icons {
      font-size: 16px !important;
      font-family: 'Material Symbols Outlined';
      color: #b45309;
      flex-shrink: 0;
      margin-top: 1px;
    }

    /* ---- Desktop layout (>768px) ---- */
    @media (min-width: 769px) {
      .settings-nav {
        width: 280px;
        flex-shrink: 0;
      }

      .nav-item-chevron {
        display: none;
      }

      .setting-item {
        flex-direction: row;
      }
    }

    /* ---- Mobile layout (<=768px) ---- */
    @media (max-width: 768px) {
      .settings-header {
        display: flex;
      }

      .settings-layout {
        flex-direction: column;
      }

      .settings-nav {
        flex: 1;
        border-right: none;
      }

      .settings-content {
        padding: 16px;
      }

      .setting-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
      }

      .setting-item.perm-row {
        flex-direction: row;
        align-items: center;
        gap: 0;
      }

      .setting-control {
        align-self: stretch;
      }

      .radio-group {
        width: 100%;
      }
    }
  `

  @state()
  private formData: SettingsFormData = {
    unitSystem: 'imperial',
    theme: 'auto',
    coordinateFormat: 'DD',
    coordinatePrecision: {
      ddDecimals: 5,
      dmsSecondDecimals: 0,
      directionFormat: 'signed' as const,
      mgrsPrecision: 5,
      utmShowZone: true,
    },
  }

  /** Current page path, set by cairn-app based on route */
  @property({ type: String })
  page = '/settings'

  @state()
  private permissionStatus: PermissionStatus | null = null

  @state()
  private permissionLoading: string | null = null

  @state()
  private isMobile = false

  private platform: 'android' | 'ios' | 'web' = 'web'

  @state()
  private authState: AuthState = authService.getState()

  @state()
  private passkeys: PasskeyInfo[] = []

  @state()
  private linkedProviders: LinkedProvider[] = []

  @state()
  private credentialsLoading = false

  @state()
  private credentialsError = ''

  @state()
  private blockedUsers: BlockedUser[] = []

  @state()
  private blockedUsersLoading = false

  private settingsStorage = new SettingsStorage()
  private resizeObserver?: ResizeObserver
  private appStateListener?: { remove: () => Promise<void> }
  private unsubAuth?: () => void

  private get currentPage(): SettingsPage {
    if (this.page === '/settings/profile') {
      return 'profile'
    }
    if (this.page === '/settings/preferences/units') {
      return 'units'
    }
    if (this.page === '/settings/preferences/theme') {
      return 'theme'
    }
    if (this.page === '/settings/preferences/tracking') {
      return 'tracking'
    }
    if (this.page === '/settings/permissions') {
      return 'permissions'
    }
    if (this.page === '/settings/privacy') {
      return 'privacy'
    }
    if (this.page === '/settings/security') {
      return 'security'
    }
    if (this.page === '/settings/storage') {
      return 'storage'
    }
    if (this.page === '/settings/subscription') {
      return 'subscription'
    }
    return 'root'
  }

  override connectedCallback(): void {
    super.connectedCallback()
    this.authState = authService.getState()
    this.unsubAuth = authService.onChange(state => {
      this.authState = state
    })
    void this.loadMaterialIcons()
    this.loadUserSettings()
    this.detectPlatform()
    this.setupAppStateListener()
    this.checkMobile()
    this.resizeObserver = new ResizeObserver((): void => this.checkMobile())
    this.resizeObserver.observe(this)

    if (this.currentPage === 'permissions') {
      void this.refreshPermissions()
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.unsubAuth?.()
    void this.appStateListener?.remove()
  }

  override updated(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has('page')) {
      if (this.currentPage === 'permissions' && !this.permissionStatus) {
        void this.refreshPermissions()
      }
      if (this.currentPage === 'security' && this.authState.status === 'authenticated') {
        void this.loadCredentials()
      }
      if (this.currentPage === 'privacy') {
        void this.loadBlockedUsers()
      }
    }
  }

  private setupAppStateListener(): void {
    // Re-check permissions when user returns from native settings
    void App.addListener('appStateChange', ({ isActive }): void => {
      if (isActive && this.currentPage === 'permissions' && this.permissionStatus) {
        void this.refreshPermissions()
      }
    }).then((handle): void => {
      this.appStateListener = handle
    })
  }

  private checkMobile(): void {
    this.isMobile = window.innerWidth <= 768
  }

  private async loadMaterialIcons(): Promise<void> {
    const materialStyle = document.createElement('style')
    materialStyle.textContent = `
      @font-face {
        font-family: 'Material Symbols Outlined';
        font-style: normal;
        font-weight: 400;
        src: url('/node_modules/@material-symbols/font-400/material-symbols-outlined.woff2') format('woff2');
      }
      .material-icons {
        font-family: 'Material Symbols Outlined';
        font-weight: normal;
        font-style: normal;
        font-size: 24px;
        line-height: 1;
        letter-spacing: normal;
        text-transform: none;
        display: inline-block;
        white-space: nowrap;
        word-wrap: normal;
        direction: ltr;
        -webkit-font-feature-settings: 'liga';
        -webkit-font-smoothing: antialiased;
      }
    `
    this.shadowRoot?.appendChild(materialStyle)
  }

  private loadUserSettings(): void {
    try {
      const settings = this.settingsStorage.getSettingsWithDefaults()
      this.populateFormData(settings)
      themeManager.initializeFromSettings(settings.theme)
    } catch (error) {
      log.error('Error loading user settings:', error)
    }
  }

  private populateFormData(settings: UserSettings): void {
    this.formData = {
      unitSystem: settings.unitSystem,
      theme: settings.theme,
      coordinateFormat: settings.coordinateFormat,
      coordinatePrecision: {
        ddDecimals: settings.coordinatePrecision?.ddDecimals ?? 5,
        dmsSecondDecimals: settings.coordinatePrecision?.dmsSecondDecimals ?? 0,
        directionFormat: settings.coordinatePrecision?.directionFormat ?? 'signed',
        mgrsPrecision: settings.coordinatePrecision?.mgrsPrecision ?? 5,
        utmShowZone: settings.coordinatePrecision?.utmShowZone ?? true,
      },
      ...(settings.contactSharing ? { contactSharing: settings.contactSharing } : {}),
    }
    this.trackQuality = settings.tracking?.trackQuality ?? 'standard'
  }

  private autoSave(): void {
    try {
      const isImperial = this.formData.unitSystem === 'imperial'
      const settings: UserSettings = {
        unitSystem: this.formData.unitSystem,
        theme: this.formData.theme,
        coordinateFormat: this.formData.coordinateFormat,
        coordinatePrecision: this.formData.coordinatePrecision,
        elevationUnit: isImperial ? 'feet' : 'meters',
        distanceUnit: isImperial ? 'miles' : 'kilometers',
        areaUnit: isImperial ? 'acres' : 'hectares',
        temperatureUnit: isImperial ? 'fahrenheit' : 'celsius',
        syncPreferences: { wifi: true, cellular: true },
        tracking: { trackQuality: this.trackQuality },
        contactSharing: this.formData.contactSharing,
      }

      const success = this.settingsStorage.saveSettings(settings)
      if (success) {
        this.dispatchEvent(
          new CustomEvent('settings-updated', {
            detail: settings,
            bubbles: true,
            composed: true,
          })
        )
        // Push account-level settings to the synced store (cross-device). Device-local
        // keys (syncPreferences) are stripped by updateSettings, so they stay per-device.
        const uid = authService.getState().userId
        if (uid) void userStorage.updateSettings(uid, settings)
      }
    } catch (error) {
      log.error('Error auto-saving settings:', error)
    }
  }

  private handleUnitSystemChange(value: 'metric' | 'imperial'): void {
    this.formData = { ...this.formData, unitSystem: value }
    this.autoSave()
    this.requestUpdate()
  }

  private handleThemeChange(value: 'light' | 'dark' | 'auto'): void {
    this.formData = { ...this.formData, theme: value }
    themeManager.setTheme(value)
    this.autoSave()
    this.requestUpdate()
  }

  private handleCoordinateFormatChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as SettingsFormData['coordinateFormat']
    this.formData = { ...this.formData, coordinateFormat: value }
    this.autoSave()
    this.requestUpdate()
  }

  private handleDdDecimalsChange(event: Event): void {
    const value = parseInt((event.target as HTMLSelectElement).value)
    this.formData = {
      ...this.formData,
      coordinatePrecision: { ...this.formData.coordinatePrecision, ddDecimals: value },
    }
    this.autoSave()
    this.requestUpdate()
  }

  private handleMgrsPrecisionChange(event: Event): void {
    const value = parseInt((event.target as HTMLSelectElement).value) as 1 | 2 | 3 | 4 | 5
    this.formData = {
      ...this.formData,
      coordinatePrecision: { ...this.formData.coordinatePrecision, mgrsPrecision: value },
    }
    this.autoSave()
    this.requestUpdate()
  }

  private handleDmsSecondDecimalsChange(event: Event): void {
    const value = parseInt((event.target as HTMLSelectElement).value)
    this.formData = {
      ...this.formData,
      coordinatePrecision: { ...this.formData.coordinatePrecision, dmsSecondDecimals: value },
    }
    this.autoSave()
    this.requestUpdate()
  }

  private handleDirectionFormatChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as 'signed' | 'cardinal'
    this.formData = {
      ...this.formData,
      coordinatePrecision: { ...this.formData.coordinatePrecision, directionFormat: value },
    }
    this.autoSave()
    this.requestUpdate()
  }

  private handleUtmShowZoneChange(event: Event): void {
    const checked =
      (event.target as HTMLElement & { selected?: boolean }).selected ??
      (event.target as HTMLInputElement).checked
    this.formData = {
      ...this.formData,
      coordinatePrecision: { ...this.formData.coordinatePrecision, utmShowZone: checked },
    }
    this.autoSave()
    this.requestUpdate()
  }

  private detectPlatform(): void {
    const p = Capacitor.getPlatform()
    if (p === 'android') {
      this.platform = 'android'
    } else if (p === 'ios') {
      this.platform = 'ios'
    } else {
      this.platform = 'web'
    }
  }

  private async refreshPermissions(): Promise<void> {
    this.permissionStatus = await permissionManager.checkAllPermissions()
  }

  private async handlePermissionClick(key: keyof PermissionStatus): Promise<void> {
    const current = this.permissionStatus?.[key]
    if (current === 'unavailable') {
      return
    }

    this.permissionLoading = key
    try {
      await permissionManager.requestPermission(key)
      await this.refreshPermissions()
    } catch (error) {
      log.error(`Failed to handle ${key} permission:`, error)
    }
    this.permissionLoading = null
  }

  @state()
  private trackQuality: 'high' | 'standard' | 'extended' = 'standard'

  @state()
  private preferencesExpanded = false

  private get headerTitle(): string {
    switch (this.currentPage) {
      case 'profile':
        return 'Profile'
      case 'units':
        return 'Units & Display'
      case 'theme':
        return 'Appearance'
      case 'tracking':
        return 'Tracking'
      case 'permissions':
        return 'Permissions'
      case 'privacy':
        return 'Privacy'
      case 'security':
        return 'Security'
      case 'storage':
        return 'Storage'
      default:
        return 'Settings'
    }
  }

  override render(): unknown {
    const page = this.currentPage

    // On desktop, show sidebar + content. On mobile, show either nav list or content page.
    const isContentPage =
      page === 'profile' ||
      page === 'units' ||
      page === 'theme' ||
      page === 'tracking' ||
      page === 'permissions' ||
      page === 'privacy' ||
      page === 'security' ||
      page === 'storage' ||
      page === 'subscription'
    const showNav = !this.isMobile || !isContentPage
    const showContent = !this.isMobile || isContentPage

    // On desktop, if at root, show profile by default
    const contentPage = !this.isMobile && !isContentPage ? 'profile' : page

    return html`
      <header class="settings-header">
        <button class="back-button" @click=${(): void => router.back()}>
          <span class="material-icons">arrow_back</span>
        </button>
        <h1 class="settings-title">${this.headerTitle}</h1>
      </header>

      <div class="settings-layout">
        ${showNav
          ? html`
              <nav class="settings-nav">
                <button
                  class="nav-item ${contentPage === 'profile' ? 'active' : ''}"
                  @click=${(): void => router.navigate('/settings/profile')}
                >
                  <span class="material-icons">person</span>
                  <span class="nav-item-text">
                    <span class="nav-item-label">Profile</span>
                    <span class="nav-item-description">Name, avatar, and contact</span>
                  </span>
                  <span class="material-icons nav-item-chevron">chevron_right</span>
                </button>

                <button
                  class="nav-item ${page === 'units' || page === 'theme' || page === 'tracking'
                    ? 'active'
                    : ''}"
                  @click=${(): void => {
                    this.preferencesExpanded = !this.preferencesExpanded
                  }}
                >
                  <span class="material-icons">tune</span>
                  <span class="nav-item-text">
                    <span class="nav-item-label">Preferences</span>
                    <span class="nav-item-description">Units, theme, and tracking</span>
                  </span>
                  <span
                    class="material-icons nav-item-chevron ${this.preferencesExpanded ||
                    page === 'units' ||
                    page === 'theme' ||
                    page === 'tracking'
                      ? 'rotated'
                      : ''}"
                    >chevron_right</span
                  >
                </button>

                <div
                  class="nav-sub-items ${this.preferencesExpanded ||
                  page === 'units' ||
                  page === 'theme' ||
                  page === 'tracking'
                    ? 'expanded'
                    : ''}"
                >
                  <button
                    class="nav-item nav-sub-item ${contentPage === 'units' ? 'active' : ''}"
                    @click=${(): void => router.navigate('/settings/preferences/units')}
                  >
                    <span class="material-icons">settings</span>
                    <span class="nav-item-text">
                      <span class="nav-item-label">Units & Display</span>
                    </span>
                  </button>
                  <button
                    class="nav-item nav-sub-item ${contentPage === 'theme' ? 'active' : ''}"
                    @click=${(): void => router.navigate('/settings/preferences/theme')}
                  >
                    <span class="material-icons">palette</span>
                    <span class="nav-item-text">
                      <span class="nav-item-label">Appearance</span>
                    </span>
                  </button>
                  <button
                    class="nav-item nav-sub-item ${contentPage === 'tracking' ? 'active' : ''}"
                    @click=${(): void => router.navigate('/settings/preferences/tracking')}
                  >
                    <span class="material-icons">route</span>
                    <span class="nav-item-text">
                      <span class="nav-item-label">Tracking</span>
                    </span>
                  </button>
                </div>

                <button
                  class="nav-item ${page === 'permissions' ? 'active' : ''}"
                  @click=${(): void => router.navigate('/settings/permissions')}
                >
                  <span class="material-icons">admin_panel_settings</span>
                  <span class="nav-item-text">
                    <span class="nav-item-label">Permissions</span>
                    <span class="nav-item-description">App permissions and access</span>
                  </span>
                  <span class="material-icons nav-item-chevron">chevron_right</span>
                </button>

                <button
                  class="nav-item ${page === 'privacy' ? 'active' : ''}"
                  @click=${(): void => router.navigate('/settings/privacy')}
                >
                  <span class="material-icons">visibility</span>
                  <span class="nav-item-text">
                    <span class="nav-item-label">Privacy</span>
                    <span class="nav-item-description">Contact sharing defaults</span>
                  </span>
                  <span class="material-icons nav-item-chevron">chevron_right</span>
                </button>

                <button
                  class="nav-item ${page === 'security' ? 'active' : ''}"
                  @click=${(): void => router.navigate('/settings/security')}
                >
                  <span class="material-icons">security</span>
                  <span class="nav-item-text">
                    <span class="nav-item-label">Security</span>
                    <span class="nav-item-description">Account and sign-in</span>
                  </span>
                  <span class="material-icons nav-item-chevron">chevron_right</span>
                </button>

                <button
                  class="nav-item ${page === 'storage' ? 'active' : ''}"
                  @click=${(): void => router.navigate('/settings/storage')}
                >
                  <span class="material-icons">storage</span>
                  <span class="nav-item-text">
                    <span class="nav-item-label">Storage</span>
                    <span class="nav-item-description">Downloads and cached data</span>
                  </span>
                  <span class="material-icons nav-item-chevron">chevron_right</span>
                </button>

                <button
                  class="nav-item ${page === 'subscription' ? 'active' : ''}"
                  @click=${(): void => router.navigate('/settings/subscription')}
                >
                  <span class="material-icons">card_membership</span>
                  <span class="nav-item-text">
                    <span class="nav-item-label">Subscription</span>
                    <span class="nav-item-description">Plan and billing</span>
                  </span>
                  <span class="material-icons nav-item-chevron">chevron_right</span>
                </button>
              </nav>
            `
          : nothing}
        ${showContent
          ? html`
              <div class="settings-content">
                <div class="content-inner">${this.renderContentPage(contentPage)}</div>
              </div>
            `
          : nothing}
      </div>
    `
  }

  private renderContentPage(page: SettingsPage): unknown {
    switch (page) {
      case 'profile':
        return html`<cairn-profile></cairn-profile>`
      case 'units':
        return this.renderUnitsSection()
      case 'theme':
        return this.renderAppearanceSection()
      case 'tracking':
        return this.renderTrackingSection()
      case 'permissions':
        return this.renderPermissionsSection()
      case 'privacy':
        return this.renderPrivacySection()
      case 'security':
        return this.renderSecuritySection()
      case 'storage':
        return html`<cairn-storage-settings></cairn-storage-settings>`
      case 'subscription':
        return html`<cairn-subscription></cairn-subscription>`
      default:
        return nothing
    }
  }

  private renderUnitsSection(): unknown {
    return html`
      <h2 class="section-title">
        <span class="material-icons">settings</span>
        Units & Display
      </h2>

      <div class="setting-item">
        <div class="setting-info">
          <div class="setting-label">Unit System</div>
          <div class="setting-description">Choose between metric and imperial units</div>
        </div>
        <div class="setting-control">
          <div class="radio-group" role="radiogroup">
            <div class="radio-option">
              <md-radio
                id="metric"
                name="unitSystem"
                value="metric"
                .checked=${this.formData.unitSystem === 'metric'}
                @change=${(): void => this.handleUnitSystemChange('metric')}
              ></md-radio>
              <label for="metric">Metric (km, m, °C)</label>
            </div>
            <div class="radio-option">
              <md-radio
                id="imperial"
                name="unitSystem"
                value="imperial"
                .checked=${this.formData.unitSystem === 'imperial'}
                @change=${(): void => this.handleUnitSystemChange('imperial')}
              ></md-radio>
              <label for="imperial">Imperial (mi, ft, °F)</label>
            </div>
          </div>
        </div>
      </div>

      <div class="setting-item">
        <div class="setting-info">
          <div class="setting-label">Coordinate Format</div>
          <div class="setting-description">How coordinates are displayed</div>
        </div>
        <div class="setting-control">
          <md-outlined-select
            label="Coordinate Format"
            .value=${this.formData.coordinateFormat}
            @change=${this.handleCoordinateFormatChange}
          >
            <md-select-option value="DD"
              ><div slot="headline">Decimal Degrees</div></md-select-option
            >
            <md-select-option value="DMS"
              ><div slot="headline">Degrees Minutes Seconds</div></md-select-option
            >
            <md-select-option value="DDM"
              ><div slot="headline">Degrees Decimal Minutes</div></md-select-option
            >
            <md-select-option value="MGRS"><div slot="headline">MGRS</div></md-select-option>
            <md-select-option value="UTM"><div slot="headline">UTM</div></md-select-option>
          </md-outlined-select>
        </div>
      </div>

      ${this.formData.coordinateFormat === 'DD' || this.formData.coordinateFormat === 'DMS'
        ? html`
            <div class="setting-item">
              <div class="setting-info">
                <div class="setting-label">Direction Format</div>
                <div class="setting-description">How positive/negative coordinates are shown</div>
              </div>
              <div class="setting-control">
                <md-outlined-select
                  label="Direction"
                  .value=${this.formData.coordinatePrecision.directionFormat}
                  @change=${this.handleDirectionFormatChange}
                >
                  <md-select-option value="signed"
                    ><div slot="headline">Signed (-45.12°)</div></md-select-option
                  >
                  <md-select-option value="cardinal"
                    ><div slot="headline">Cardinal (45.12° N)</div></md-select-option
                  >
                </md-outlined-select>
              </div>
            </div>
          `
        : nothing}
      ${this.formData.coordinateFormat === 'DD'
        ? html`
            <div class="setting-item">
              <div class="setting-info">
                <div class="setting-label">Decimal Places</div>
                <div class="setting-description">Precision for decimal degrees (5 ≈ 1.1m)</div>
              </div>
              <div class="setting-control">
                <md-outlined-select
                  label="Decimals"
                  .value=${String(this.formData.coordinatePrecision.ddDecimals)}
                  @change=${this.handleDdDecimalsChange}
                >
                  <md-select-option value="2"
                    ><div slot="headline">2 (~1.1km)</div></md-select-option
                  >
                  <md-select-option value="3"
                    ><div slot="headline">3 (~110m)</div></md-select-option
                  >
                  <md-select-option value="4"><div slot="headline">4 (~11m)</div></md-select-option>
                  <md-select-option value="5"
                    ><div slot="headline">5 (~1.1m)</div></md-select-option
                  >
                </md-outlined-select>
              </div>
            </div>
          `
        : nothing}
      ${this.formData.coordinateFormat === 'DMS'
        ? html`
            <div class="setting-item">
              <div class="setting-info">
                <div class="setting-label">Seconds Precision</div>
                <div class="setting-description">Decimal places for seconds</div>
              </div>
              <div class="setting-control">
                <md-outlined-select
                  label="Decimals"
                  .value=${String(this.formData.coordinatePrecision.dmsSecondDecimals)}
                  @change=${this.handleDmsSecondDecimalsChange}
                >
                  <md-select-option value="0"><div slot="headline">0 (~30m)</div></md-select-option>
                  <md-select-option value="1"><div slot="headline">1 (~3m)</div></md-select-option>
                  <md-select-option value="2"
                    ><div slot="headline">2 (~0.3m)</div></md-select-option
                  >
                </md-outlined-select>
              </div>
            </div>
          `
        : nothing}
      ${this.formData.coordinateFormat === 'MGRS'
        ? html`
            <div class="setting-item">
              <div class="setting-info">
                <div class="setting-label">Grid Precision</div>
                <div class="setting-description">MGRS grid square resolution</div>
              </div>
              <div class="setting-control">
                <md-outlined-select
                  label="Precision"
                  .value=${String(this.formData.coordinatePrecision.mgrsPrecision)}
                  @change=${this.handleMgrsPrecisionChange}
                >
                  <md-select-option value="1"><div slot="headline">10km</div></md-select-option>
                  <md-select-option value="2"><div slot="headline">1km</div></md-select-option>
                  <md-select-option value="3"><div slot="headline">100m</div></md-select-option>
                  <md-select-option value="4"><div slot="headline">10m</div></md-select-option>
                  <md-select-option value="5"><div slot="headline">1m</div></md-select-option>
                </md-outlined-select>
              </div>
            </div>
          `
        : nothing}
      ${this.formData.coordinateFormat === 'UTM'
        ? html`
            <div class="setting-item">
              <div class="setting-info">
                <div class="setting-label">Show Zone</div>
                <div class="setting-description">Include UTM zone number and letter</div>
              </div>
              <div class="setting-control">
                <md-switch
                  aria-label="Show UTM zone"
                  .selected=${this.formData.coordinatePrecision.utmShowZone}
                  @change=${this.handleUtmShowZoneChange}
                ></md-switch>
              </div>
            </div>
          `
        : nothing}
    `
  }

  private renderAppearanceSection(): unknown {
    return html`
      <h2 class="section-title">
        <span class="material-icons">palette</span>
        Appearance
      </h2>

      <div class="setting-item">
        <div class="setting-info">
          <div class="setting-label">Theme</div>
          <div class="setting-description">Choose your preferred app theme</div>
        </div>
        <div class="setting-control">
          <div class="radio-group" role="radiogroup">
            <div class="radio-option">
              <md-radio
                id="light"
                name="theme"
                value="light"
                .checked=${this.formData.theme === 'light'}
                @change=${(): void => this.handleThemeChange('light')}
              ></md-radio>
              <label for="light">Light</label>
            </div>
            <div class="radio-option">
              <md-radio
                id="auto"
                name="theme"
                value="auto"
                .checked=${this.formData.theme === 'auto'}
                @change=${(): void => this.handleThemeChange('auto')}
              ></md-radio>
              <label for="auto">Auto (follows system)</label>
            </div>
            <div class="radio-option">
              <md-radio
                id="dark"
                name="theme"
                value="dark"
                .checked=${this.formData.theme === 'dark'}
                @change=${(): void => this.handleThemeChange('dark')}
              ></md-radio>
              <label for="dark">Dark</label>
            </div>
          </div>
        </div>
      </div>
    `
  }

  private renderTrackingSection(): unknown {
    return html`
      <h2 class="section-title">
        <span class="material-icons">route</span>
        Tracking
      </h2>

      <div class="setting-item">
        <div class="setting-info">
          <div class="setting-label">Track Quality</div>
          <div class="setting-description">
            Controls how frequently track points are recorded. Higher quality uses more battery.
          </div>
        </div>
        <div class="setting-control">
          <div class="radio-group" role="radiogroup">
            <div class="radio-option">
              <md-radio
                id="track-high"
                name="trackQuality"
                value="high"
                .checked=${this.trackQuality === 'high'}
                @change=${(): void => this.handleTrackQualityChange('high')}
              ></md-radio>
              <label for="track-high"
                >High — Best detail, more battery (${this.formatDisplacement(3)})</label
              >
            </div>
            <div class="radio-option">
              <md-radio
                id="track-standard"
                name="trackQuality"
                value="standard"
                .checked=${this.trackQuality === 'standard'}
                @change=${(): void => this.handleTrackQualityChange('standard')}
              ></md-radio>
              <label for="track-standard"
                >Standard — Balanced (${this.formatDisplacement(5)})</label
              >
            </div>
            <div class="radio-option">
              <md-radio
                id="track-extended"
                name="trackQuality"
                value="extended"
                .checked=${this.trackQuality === 'extended'}
                @change=${(): void => this.handleTrackQualityChange('extended')}
              ></md-radio>
              <label for="track-extended"
                >Extended — Maximum battery life (${this.formatDisplacement(10)})</label
              >
            </div>
          </div>
        </div>
      </div>
    `
  }

  private formatDisplacement(meters: number): string {
    if (this.formData.unitSystem === 'imperial') {
      return `${Math.round(meters * 3.28084)}ft`
    }
    return `${meters}m`
  }

  private handleTrackQualityChange(value: 'high' | 'standard' | 'extended'): void {
    this.trackQuality = value
    this.autoSave()
  }

  private renderPermissionsSection(): unknown {
    if (!this.permissionStatus) {
      return html`
        <h2 class="section-title">
          <span class="material-icons">admin_panel_settings</span>
          Permissions
        </h2>
        <div class="setting-description">Loading permissions...</div>
      `
    }

    return html`
      ${this.renderPermissionItem(
        'location',
        'Location',
        this.platform === 'web'
          ? 'Access your location for map positioning'
          : 'GPS access for navigation and tracking',
        'location_on'
      )}
      ${this.renderLocationWarnings()}
      ${this.platform !== 'web'
        ? html`
            ${this.renderPermissionItem(
              'storage',
              'Storage',
              this.platform === 'android'
                ? 'Read and write files for GPX import/export'
                : 'Access files for GPX import/export',
              'folder'
            )}
            ${this.platform === 'android'
              ? this.renderPermissionItem(
                  'activityRecognition',
                  'Activity Recognition',
                  'Auto-detect activity type (hike, run, bike) during recording',
                  'directions_walk'
                )
              : nothing}
          `
        : nothing}
    `
  }

  private renderLocationWarnings(): unknown {
    if (this.permissionStatus?.location !== 'granted') {
      return nothing
    }
    if (this.platform === 'web') {
      return nothing
    }

    const warnings: ReturnType<typeof html>[] = []

    if (!this.permissionStatus.fineLocation) {
      warnings.push(html`
        <div class="perm-warning">
          <span class="material-icons">warning</span>
          <span
            >Only approximate location is enabled. For accurate navigation, set location to
            <strong>Precise</strong> in settings.</span
          >
        </div>
      `)
    }

    if (!this.permissionStatus.backgroundLocation) {
      warnings.push(html`
        <div class="perm-warning">
          <span class="material-icons">warning</span>
          <span
            >Background location is not enabled. To track while the app is minimized, set location
            to <strong>Allow all the time</strong> in settings.</span
          >
        </div>
      `)
    }

    return warnings
  }

  private renderPermissionItem(
    key: keyof PermissionStatus,
    label: string,
    description: string,
    icon: string
  ): unknown {
    const status = this.permissionStatus?.[key]
    const isLoading = this.permissionLoading === key

    let state: 'granted' | 'denied' | 'unavailable'
    let statusIcon: string
    let title: string

    if (status === 'unavailable') {
      state = 'unavailable'
      statusIcon = 'block'
      title = 'Not available on this device'
    } else if (status === 'granted' || status === true) {
      state = 'granted'
      statusIcon = 'check_circle'
      title = 'Permitted — tap to change in settings'
    } else {
      state = 'denied'
      statusIcon = 'do_not_disturb_on'
      title = 'Not granted — tap to request'
    }

    const isClickable = state !== 'unavailable'

    return html`
      <div class="setting-item perm-row">
        <div class="setting-info">
          <div class="setting-label" style="display: flex; align-items: center; gap: 6px;">
            <span
              class="material-icons"
              style="font-size: 18px; font-family: 'Material Symbols Outlined'; color: var(--cairn-text-secondary-color, #64748b);"
              >${icon}</span
            >
            ${label}
          </div>
          <div class="setting-description">${description}</div>
        </div>
        <div class="setting-control">
          <md-icon-button
            class="perm-icon-btn ${state}"
            title=${title}
            ?disabled=${!isClickable || isLoading}
            @click=${isClickable ? (): Promise<void> => this.handlePermissionClick(key) : nothing}
          >
            <span class="material-icons">${isLoading ? 'hourglass_top' : statusIcon}</span>
          </md-icon-button>
        </div>
      </div>
    `
  }
  private async loadCredentials(): Promise<void> {
    if (this.credentialsLoading) return
    this.credentialsLoading = true
    this.credentialsError = ''

    try {
      const data = await listCredentials()
      this.passkeys = data.passkeys
      this.linkedProviders = data.providers
    } catch (err) {
      log.error('Failed to load credentials:', err)
      this.credentialsError = 'Failed to load credentials'
    } finally {
      this.credentialsLoading = false
    }
  }

  private async handleAddPasskey(): Promise<void> {
    try {
      this.credentialsError = ''
      await registerPasskey()
      await this.loadCredentials()
    } catch (err) {
      log.error('Failed to register passkey:', err)
      this.credentialsError = `Failed to register passkey: ${err instanceof Error ? err.message : String(err)}`
    }
  }

  private async handleDeletePasskey(credentialId: string): Promise<void> {
    try {
      await deletePasskey(credentialId)
      this.passkeys = this.passkeys.filter(p => p.credentialId !== credentialId)
    } catch (err) {
      log.error('Failed to delete passkey:', err)
      this.credentialsError = 'Failed to delete passkey'
    }
  }

  private async loadBlockedUsers(): Promise<void> {
    if (this.blockedUsersLoading) return
    this.blockedUsersLoading = true
    try {
      const { getBlockedUsers } = await import('../../services/moderation/moderation-api.js')
      this.blockedUsers = await getBlockedUsers()
    } catch (err) {
      log.error('Failed to load blocked users:', err)
    } finally {
      this.blockedUsersLoading = false
    }
  }

  private async handleUnblockUser(userId: string): Promise<void> {
    const success = await blockStore.unblock(userId)
    if (success) {
      this.blockedUsers = this.blockedUsers.filter(u => u.userId !== userId)
    }
  }

  // Brand SVG icons — monochrome, styled to match Material Symbols
  private readonly googleIcon = html`<svg viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      opacity=".7"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      opacity=".5"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.97 10.97 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"
      opacity=".6"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>`
  private readonly appleIcon = html`<svg viewBox="0 0 24 24">
    <path
      d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
    />
  </svg>`
  private readonly facebookIcon = html`<svg viewBox="0 0 24 24">
    <path
      d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047v-2.66c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.971H15.83c-1.491 0-1.956.93-1.956 1.886v2.264h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"
    />
  </svg>`
  private readonly emailIcon = html`<span class="material-icons">mail</span>`
  private readonly passkeyIcon = html`<span class="material-icons">fingerprint</span>`

  /** Map Cognito provider names to display labels. */
  private providerLabel(name: string): string {
    const labels: Record<string, string> = {
      Google: 'Google',
      SignInWithApple: 'Apple',
      Facebook: 'Facebook',
    }
    return labels[name] ?? name
  }

  private providerSvg(name: string): unknown {
    if (name === 'Google') return this.googleIcon
    if (name === 'SignInWithApple') return this.appleIcon
    if (name === 'Facebook') return this.facebookIcon
    return this.emailIcon
  }

  private renderPrivacySection(): unknown {
    const sharing = this.formData.contactSharing ?? {
      shareEmail: 'ask',
      sharePhone: 'ask',
      shareEmergency: 'ask',
    }

    const renderOption = (
      field: 'shareEmail' | 'sharePhone' | 'shareEmergency',
      label: string,
      icon: string,
      description: string
    ): unknown => {
      const current = sharing[field] ?? 'ask'
      return html`
        <div class="setting-group">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
            <span class="material-icons" style="font-size: 18px;">${icon}</span>
            <span style="font-weight: 500;">${label}</span>
          </div>
          <div class="setting-description" style="margin-bottom: 8px;">${description}</div>
          <div style="display: flex; gap: 4px;">
            ${(['always', 'ask', 'never'] as const).map(
              value => html`
                <button
                  style="
                    padding: 6px 14px;
                    border-radius: 16px;
                    border: 1px solid ${current === value
                    ? 'var(--cairn-primary-color, #3b82f6)'
                    : 'var(--cairn-border-color, #e2e8f0)'};
                    background: ${current === value ? 'rgba(59, 130, 246, 0.1)' : 'transparent'};
                    color: ${current === value
                    ? 'var(--cairn-primary-color, #3b82f6)'
                    : 'var(--cairn-text-color, #1e293b)'};
                    font-size: 13px;
                    font-weight: ${current === value ? '600' : '400'};
                    cursor: pointer;
                    text-transform: capitalize;
                  "
                  @click=${(): void => {
                    this.formData = {
                      ...this.formData,
                      contactSharing: { ...sharing, [field]: value },
                    }
                    this.autoSave()
                  }}
                >
                  ${value}
                </button>
              `
            )}
          </div>
        </div>
      `
    }

    return html`
      <h2 class="section-title">
        <span class="material-icons">visibility</span>
        Privacy
      </h2>
      <div class="setting-description" style="margin-bottom: 16px;">
        Choose what contact information to share by default when you join a plan. These defaults can
        be overridden for each plan individually.
      </div>
      ${renderOption('shareEmail', 'Email', 'email', 'Share your email address with plan members')}
      ${renderOption('sharePhone', 'Phone', 'phone', 'Share your phone number with plan members')}
      ${renderOption(
        'shareEmergency',
        'Emergency Contact',
        'emergency',
        'Share your emergency contact with plan members'
      )}

      <hr class="section-divider" />

      <h2 class="section-title">
        <span class="material-icons">block</span>
        Blocked Users
      </h2>
      ${this.blockedUsersLoading
        ? html`<div class="setting-description">Loading...</div>`
        : this.blockedUsers.length === 0
          ? html`<div class="setting-description">No blocked users.</div>`
          : this.blockedUsers.map(
              u => html`
                <div class="setting-item">
                  <div class="setting-info">
                    <div class="setting-label">${u.email ?? u.userId.slice(0, 12)}</div>
                    <div class="setting-description">
                      Blocked ${new Date(u.blockedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div class="setting-control">
                    <md-outlined-button
                      @click=${(): Promise<void> => this.handleUnblockUser(u.userId)}
                    >
                      Unblock
                    </md-outlined-button>
                  </div>
                </div>
              `
            )}
    `
  }

  private renderSecuritySection(): unknown {
    const isAuthenticated = this.authState.status === 'authenticated'

    if (!isAuthenticated) {
      return html`
        <h2 class="section-title">
          <span class="material-icons">security</span>
          Security
        </h2>
        <div class="setting-item">
          <div class="setting-info">
            <div class="setting-label">Guest mode</div>
            <div class="setting-description">
              Sign in to sync your data across devices and access your account from anywhere.
            </div>
          </div>
          <div class="setting-control">
            <md-icon-button @click=${(): Promise<void> => authService.login()}>
              <span class="material-icons">login</span>
            </md-icon-button>
          </div>
        </div>
      `
    }

    const linkedNames = new Set(this.linkedProviders.map(p => p.name))
    const allProviders = ['Google', 'SignInWithApple', 'Facebook'] as const
    const availableProviders = new Set(['Google'])
    const comingSoon = new Set(['SignInWithApple', 'Facebook'])

    return html`
      <!-- Linked Accounts -->
      <h2 class="section-title">
        <span class="material-icons">link</span>
        Linked Accounts
      </h2>

      ${this.credentialsLoading
        ? html`<div class="credential-row">
            <div class="credential-detail">Loading...</div>
          </div>`
        : html`
            <!-- Email (always linked for authenticated users) -->
            <div class="credential-row">
              <div class="credential-icon">${this.emailIcon}</div>
              <div class="credential-info">
                <div class="credential-name">Email</div>
                <div class="credential-detail">${this.authState.email ?? ''}</div>
              </div>
              <div class="credential-status">
                <span class="material-icons">check_circle</span>
              </div>
            </div>

            <!-- Linked providers -->
            ${this.linkedProviders.map(
              provider => html`
                <div class="credential-row">
                  <div class="credential-icon">${this.providerSvg(provider.name)}</div>
                  <div class="credential-info">
                    <div class="credential-name">${this.providerLabel(provider.name)}</div>
                    <div class="credential-detail">${provider.email ?? 'Connected'}</div>
                  </div>
                  <div class="credential-status">
                    <span class="material-icons">check_circle</span>
                  </div>
                </div>
              `
            )}

            <!-- Unlinked providers -->
            ${allProviders
              .filter(name => !linkedNames.has(name))
              .map(
                name => html`
                  <div class="credential-row">
                    <div class="credential-icon">${this.providerSvg(name)}</div>
                    <div class="credential-info">
                      <div class="credential-name">${this.providerLabel(name)}</div>
                      <div class="credential-detail">
                        ${comingSoon.has(name) ? 'Coming soon' : 'Not linked'}
                      </div>
                    </div>
                    <md-outlined-button
                      ?disabled=${!availableProviders.has(name)}
                      @click=${(): void => {
                        /* TODO: initiate linking flow */
                      }}
                    >
                      Link
                    </md-outlined-button>
                  </div>
                `
              )}
          `}

      <hr class="section-divider" />

      <!-- Passkeys -->
      <h2 class="section-title">
        <span class="material-icons">passkey</span>
        Passkeys
      </h2>

      ${this.credentialsLoading
        ? html`<div class="credential-row">
            <div class="credential-detail">Loading...</div>
          </div>`
        : html`
            ${this.passkeys.length === 0
              ? html`<div class="credential-detail" style="padding: 8px 0;">
                  No passkeys registered. Add one for quick biometric sign-in.
                </div>`
              : this.passkeys.map(
                  pk => html`
                    <div class="credential-row">
                      <div class="credential-icon">${this.passkeyIcon}</div>
                      <div class="credential-info">
                        <div class="credential-name">${pk.friendlyName ?? 'Passkey'}</div>
                        <div class="credential-detail">
                          ${pk.createdAt
                            ? `Added ${new Date(pk.createdAt).toLocaleDateString()}`
                            : ''}
                        </div>
                      </div>
                      <md-icon-button
                        @click=${(): Promise<void> => this.handleDeletePasskey(pk.credentialId)}
                      >
                        <span class="material-icons">delete</span>
                      </md-icon-button>
                    </div>
                  `
                )}

            <div class="credential-row">
              <div class="credential-icon">
                <span class="material-icons">key</span>
              </div>
              <div class="credential-info">
                <div class="credential-name">Add passkey</div>
                <div class="credential-detail">Fingerprint, face, or security key</div>
              </div>
              <md-outlined-button @click=${(): Promise<void> => this.handleAddPasskey()}>
                Add
              </md-outlined-button>
            </div>
          `}
      ${this.credentialsError
        ? html`<div
            style="color: var(--cairn-error-color, #dc2626); margin-top: 8px; font-size: 13px;"
          >
            ${this.credentialsError}
          </div>`
        : nothing}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cairn-settings': CairnSettings
  }
}
